import { getEmbedValidationSchema } from "@circle-react-shared/uikit/TipTapBlockEditor/EmbedExtension/constants";
import type { AddToLocalSgidToObjectMap } from "@circle-react-shared/uikit/TipTapBlockEditor/EmbedExtension/types";
import { useCreateEmbed } from "@circle-react-shared/uikit/TipTapBlockEditor/EmbedExtension/useCreateEmbed";
import { TextInput } from "@circleco/compass/components/TextInput";
import type { Editor } from "@tiptap/core";
import { noop } from "lodash";
import { useState } from "react";
import type { ChangeEvent, FormEvent } from "react";

import { t } from "@/i18n-js/instance";

export const getEmbedDescription = (provider?: string) =>
  provider
    ? `${t("tiptap.extensions.embed.provider_description.works_with", {
        provider,
      })}${t("tiptap.extensions.embed.provider_description.providers")}`
    : `${t("tiptap.extensions.embed.description.start")}${t(
        "tiptap.extensions.embed.description.providers",
      )}${t("tiptap.extensions.embed.description.end")}`;

interface UseEmbedFormParams {
  addToLocalSgidToObjectMap?: AddToLocalSgidToObjectMap;
  editor: Editor;
  range: { from: number; to: number };
}

export const useEmbedForm = ({
  addToLocalSgidToObjectMap = noop,
  editor,
  range,
}: UseEmbedFormParams) => {
  const [url, setUrl] = useState("");
  const [error, setError] = useState<string>();
  const createEmbedMutation = useCreateEmbed({
    addToLocalSgidToObjectMap,
    editor,
    range,
  });

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    setUrl(event.target.value);
    setError(undefined);
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const trimmedUrl = url.trim();
    if (!trimmedUrl || createEmbedMutation.isPending) return;
    if (!getEmbedValidationSchema().isValidSync({ url: trimmedUrl })) {
      setError(t("courses.edit.lesson.embed_invalid_url"));
      return;
    }
    createEmbedMutation.mutate(trimmedUrl);
  };

  return {
    error,
    handleChange,
    handleSubmit,
    isEmpty: url.trim().length === 0,
    isLoading: createEmbedMutation.isPending,
    url,
  };
};

interface EmbedFormProps {
  error?: string;
  formId: string;
  onChange: (event: ChangeEvent<HTMLInputElement>) => void;
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
  url: string;
}

export const EmbedForm = ({
  error,
  formId,
  onChange,
  onSubmit,
  url,
}: EmbedFormProps) => (
  <form id={formId} onSubmit={onSubmit} className="p-1">
    <TextInput
      autoFocus
      value={url}
      onChange={onChange}
      invalid={Boolean(error)}
      error={error}
      placeholder={t("courses.edit.lesson.embed_placeholder")}
    />
  </form>
);
